/**
 * Sending a clinician who opened a signing link under the wrong account back
 * through login as the account the order is assigned to.
 *
 * One person often holds several accounts whose addresses share an inbox
 * (``name+a@…``, ``name+b@…``). The signing email goes to the assigned
 * clinician's address, but the browser may still hold a session for another of
 * them, and provider-api will only mint for the clinician of record. Telling
 * that person they are "not authorized" is wrong — they are, under a different
 * login — so the page signs them out and the login page offers the right one.
 *
 * The hand-off survives the Auth0 logout round trip in sessionStorage rather
 * than the logout ``returnTo``: that URL must match the tenant's allow-list
 * exactly, and plain ``/login`` is the one every logout in the app already uses.
 */

const STORAGE_KEY = 'inflm.signer-account-switch'

/** Long enough to read the email and sign in; short enough not to prefill a later, unrelated login. */
const MAX_AGE_MS = 15 * 60 * 1000

export interface SignerAccountSwitch {
  email: string
  returnTo: string
}

const sameEmail = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase()

/**
 * Whether signing out to log in as ``signerEmail`` could put the right account
 * in front of the ceremony.
 *
 * Never when the session already is that address: logging that user out would
 * bring them straight back to the same refusal, so a genuine one is left to
 * surface as an error.
 */
export const canSwitchToSigner = (signerEmail: string | null | undefined, sessionEmail: string): boolean => {
  const email = signerEmail?.trim() ?? ''
  return email !== '' && !sameEmail(email, sessionEmail)
}

export const rememberSignerAccountSwitch = (handoff: SignerAccountSwitch): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...handoff, at: Date.now() }))
  } catch {
    // Storage unavailable: the login page simply opens without a suggestion.
  }
}

export const readSignerAccountSwitch = (): SignerAccountSwitch | null => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null') as {
      email?: unknown
      returnTo?: unknown
      at?: unknown
    } | null
    if (!parsed || typeof parsed.email !== 'string' || typeof parsed.returnTo !== 'string') return null
    if (typeof parsed.at !== 'number' || Date.now() - parsed.at > MAX_AGE_MS) return null
    // Only ever a path on this origin, so a tampered value cannot redirect off-site.
    if (!parsed.returnTo.startsWith('/') || parsed.returnTo.startsWith('//')) return null
    return { email: parsed.email, returnTo: parsed.returnTo }
  } catch {
    return null
  }
}

export const clearSignerAccountSwitch = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to clear.
  }
}
